import { create } from "zustand";
import { toggleMotionPick, type PaymentItem, type ConsentRequest } from "@fiket/booth-schema";
import { DEFAULT_LANGUAGE, type Language } from "../i18n/locale";
import { languageOnBoothConfig, languageOnReset } from "../i18n/boothLanguage";
import { bindActivitySnapshot, track, trackRoute, trackVisitEnd } from "../telemetry/activity";
import { visitTransition } from "../telemetry/activityQueue";

/** 키오스크 UI 언어 — schema LocaleCode(ko/en/ja/zh). 코드·라벨·기본값은 schema `i18n.ts` 가 출처(i18n/locale.ts 는 재export) */
export type { Language };

/**
 * 스테이지 플로우: attract → themeSelect → styleSelect → preview(촬영) → generate → review
 *   → (videoIntro? → videoMotions? → videoCaption)? → (payment)? → (videoGenerate)? → printShare → (print)? → thanks
 * videoGenerate 는 영상 job 을 **시작만** 하고 바로 printShare 로 간다(BOOTH-60 지연 전달) — 완료는 서버가 세션에 붙이고
 * 받기 화면·QR 공유 페이지가 폴링해 보여준다. 옛 videoReview(완료 대기·미리보기) 는 제거됨.
 * payment 는 결제가 걸리는 부스에서만 — 사진 결제(photoPaymentActive)는 printShare 직전, 영상 생성 직전엔 사진·영상 어느 쪽이든
 * 걸리면 끼운다 (BOOTH-61/74/80). 사진 결제는 기본 OFF 라 보통 결제 QR 은 영상 생성 직전에만 나온다.
 * 분기 규칙은 `features/Payment/paymentFlow.ts`.
 * videoIntro 는 샘플 영상 루프로 "움직이는 화보" 를 소개하는 화면(캔버스 9.1) — 테마·부스 샘플이 둘 다 없으면 건너뛴다.
 * videoMotions 는 동작 1~2개를 순서대로 고르는 화면(BOOTH-60 1b, 캔버스 9.2) — 활성 동작이 없으면 건너뛴다.
 * videoCaption 은 영상 아래 종이색 띠에 들어갈 한 줄(최대 12자)을 받는 화면(BOOTH-85, 캔버스 9.3) — 결제 뒤엔 되돌아가지 않으므로
 *   자막은 결제 전에 고정된다. 키오스크가 캔버스로 PNG 를 그려 store 에 두고 videoGenerate 가 본문에 싣는다.
 * print 는 "인쇄하기" 를 눌렀을 때만 거치는 인쇄 중 화면(BOOTH-39, 현재 window.print 스텁).
 * 예전 start(안내)·frameSelect(프레임) 스테이지는 담다 UI 이식 때 제거됨.
 * themeSelect 는 테마 → 스타일 2단계 계층(BOOTH-12)으로 추가.
 * consent 는 개인정보 동의 화면(BOOTH-66) — styleSelect 다음, 동의서가 있고 아직 동의 전일 때만 거친다.
 */
export type Route =
  | "attract"
  | "themeSelect"
  | "styleSelect"
  | "consent"
  | "preview"
  | "generate"
  | "review"
  | "videoIntro"
  | "videoMotions"
  | "videoCaption"
  | "videoGenerate"
  | "payment"
  | "printShare"
  | "print"
  | "thanks";

/** 진행 표시(journey) 단계 수 — 테마 선택 · 스타일 선택 · 사진 촬영 · 결과 확인 · 사진 받기 */
export const JOURNEY_STEPS = 5;
export type JourneyStep = 1 | 2 | 3 | 4 | 5;

/** 진행 표시(journey) 단계. attract/thanks 는 표시 안 함(null). */
export function journeyStep(route: Route): JourneyStep | null {
  switch (route) {
    case "themeSelect": return 1;
    case "styleSelect": return 2;
    case "consent":
    case "preview": return 3;
    case "generate":
    case "review":
    case "videoIntro":
    case "videoMotions":
    case "videoCaption":
    case "videoGenerate": return 4;
    case "payment":
    case "printShare":
    case "print": return 5;
    default: return null;
  }
}

export type TransformStatus = "pending" | "generating" | "done" | "error";

/** 결제 화면을 취소·만료로 떠난 이유 — ReviewView 가 한 줄 안내 후 지운다 (BOOTH-61/74) */
export type PaymentExitReason = "expired" | "cancelled";

export interface CapturedShot {
  id: string;
  index: number;
  capturedAt: string;
  dataUrl: string;
  transformedDataUrl: string | null;
  transformStatus: TransformStatus;
  /**
   * 워터마크 없는 변환 결과 사본 (BOOTH-103) — 영상 생성 입력에 쓴다.
   * 워터마크 합성 전 `clean` dataUrl. 워터마크가 없는 부스는 `transformedDataUrl` 과 같은 문자열.
   * 변환 중 또는 결과 없음이면 null.
   */
  videoInputDataUrl: string | null;
}

export const TOTAL_SHOTS = 1;

export type VideoStatus = "idle" | "generating" | "ready" | "failed";

interface RouterState {
  route: Route;
  selectedThemeId: string | null;
  selectedStyleId: string | null;
  capturedShots: CapturedShot[];
  lastShortCode: string | null;
  /**
   * 세션 공유 페이지 주소(`/p/:code`, 서버가 준 그대로 — `?lang=` 은 `shareQrUrl` 이 붙인다). 사진 받기에서 발급되고
   * 인쇄·thanks 화면이 같은 QR 을 이어서 보여준다 (BOOTH-63). resetSession 으로 지워진다
   */
  downloadUrl: string | null;
  /** agent 모드 인쇄 작업 ID — PrintShare 가 생성해 PrintView 가 폴링. resetSession 으로 지워진다 */
  printId: string | null;
  language: Language;
  /**
   * 부스 설정의 기본 언어 (BOOTH-116) — `/api/booth/:slug/config` 를 받을 때마다 갱신된다.
   * 새 손님이 보는 언어의 출처: 어트랙트에선 즉시 `language` 로, 세션 중이면 `resetSession` 때 복귀한다.
   * `INITIAL` 에 넣지 않아 세션 리셋으로 지워지지 않는다.
   */
  boothLanguage: Language;
  videoStatus: VideoStatus;
  videoUrl: string | null;
  videoJobId: string | null;
  /** 손님이 고른 영상 동작 id, 고른 순서대로 (MOTION_PICK_MIN~MOTION_PICK_MAX 개). 서버가 이 순서로 프롬프트를 합성한다. */
  selectedMotionIds: string[];
  /** 손님이 남긴 자막(정규화, 1~CAPTION_MAX_GRAPHEMES 자) — 없으면 null (BOOTH-85). `clearVideo` 는 유지(다시 만들기), 재촬영·resetSession 이 지운다 */
  videoCaption: string | null;
  /** 자막 PNG dataURL(1440×480 투명, 키오스크 캔버스 렌더 — 스타일별 서체·색·효과) — `videoCaption` 과 항상 짝. 서버 ffmpeg 가 얹는다 */
  videoCaptionImage: string | null;
  /** 승인된 결제 — PaymentView 가 paid 를 확인하면 세팅. 세션 생성·영상 시작 본문에 실리고 resetSession 이 지운다 (BOOTH-61/74) */
  paymentId: string | null;
  /** 이번 결제가 영상 생성 직전 결제인지 — paid 뒤 갈 곳(videoGenerate | printShare) 을 정한다. 항목 구성은 서버가 정한다 */
  paymentWithVideo: boolean;
  /**
   * 승인된 결제에 영상 항목이 실제로 들어갔는지 — 생성 응답 items 스냅숏 (BOOTH-80). 사진 ON + 영상 결제 OFF 부스는
   * 영상 생성 직전 결제에도 영상 항목이 없어 영상 실패 환불 안내·재시도 숨김을 `paymentWithVideo` 로 판단하면 틀린다.
   */
  paymentVideoCharged: boolean;
  /** 결제 화면을 취소·만료로 떠난 이유 — ReviewView 가 한 줄 안내 후 지운다 */
  paymentExit: PaymentExitReason | null;
  /**
   * 손님 1회 방문 id (BOOTH-87 통계) — 어트랙트를 떠날 때 발급, resetSession 이 지운다.
   * 활동 이벤트·세션·변환·영상 요청에 실려 한 퍼널로 묶인다.
   */
  visitId: string | null;
  /**
   * 이번 방문에서 발급된 동의 ID — 서버가 동의 제출 후 돌려준다. consentId 가 있으면 styleSelect CTA 가 preview 로 직행한다.
   * resetSession 으로 지워진다 (BOOTH-66).
   */
  consentId: string | null;
  /**
   * 동의 화면이 서버로 보낼 ConsentRequest — 동의 화면 진입 시 세팅, 제출 후 지운다. resetSession 으로 지워진다.
   */
  consentPending: ConsentRequest | null;

  go: (next: Route) => void;
  setLanguage: (lang: Language) => void;
  /** 부스 기본 언어 반영 (BOOTH-116) — 어트랙트면 화면 언어도 즉시 바꾼다. 손님 선택이 아니므로 텔레메트리 없음 */
  setBoothLanguage: (lang: Language) => void;
  setTheme: (id: string | null) => void;
  setStyle: (id: string | null) => void;
  addShot: (
    shot: Omit<CapturedShot, "transformedDataUrl" | "transformStatus" | "videoInputDataUrl">
  ) => void;
  setTransformed: (
    id: string,
    dataUrl: string | null,
    status: TransformStatus,
    /** 워터마크 없는 사본 (BOOTH-103). 생략하면 null. */
    videoInputDataUrl?: string | null
  ) => void;
  clearShots: () => void;
  setShortCode: (code: string, downloadUrl: string) => void;
  setPrintId: (id: string | null) => void;
  resetSession: () => void;
  setVideoJob: (jobId: string) => void;
  setVideoReady: (url: string) => void;
  setVideoFailed: () => void;
  /** 영상 job 상태만 초기화 — 고른 동작은 유지(다시 만들기 시 같은 동작으로). */
  clearVideo: () => void;
  /** 동작 선택 토글 (`toggleMotionPick` 규칙 — 재탭 해제, 상한이면 무시). */
  toggleMotion: (id: string) => void;
  clearMotions: () => void;
  /** 자막 확정 — 정규화된 문자열과 캔버스 PNG dataURL 을 함께 둔다 */
  setVideoCaption: (text: string, image: string) => void;
  /** 자막 없이 진행("넣지 않기") 또는 재촬영 */
  clearVideoCaption: () => void;
  /** 결제 화면 진입 직전 — 항목 구성을 기억하고 이전 결제·안내를 지운다 */
  beginPayment: (withVideo: boolean) => void;
  setPaymentPaid: (paymentId: string, items: readonly PaymentItem[]) => void;
  setPaymentExit: (reason: PaymentExitReason) => void;
  clearPaymentExit: () => void;
  clearPayment: () => void;
  /** 동의 완료 — 서버가 발급한 consentId 를 저장하고 pending 을 지운다 */
  setConsentId: (id: string) => void;
  /** 동의 화면 진입 시 저장할 요청 세팅 또는 제거 */
  setConsentPending: (req: ConsentRequest | null) => void;
}

const INITIAL: Pick<
  RouterState,
  | "route"
  | "selectedThemeId"
  | "selectedStyleId"
  | "capturedShots"
  | "lastShortCode"
  | "downloadUrl"
  | "printId"
  | "language"
  | "videoStatus"
  | "videoUrl"
  | "videoJobId"
  | "selectedMotionIds"
  | "videoCaption"
  | "videoCaptionImage"
  | "paymentId"
  | "paymentWithVideo"
  | "paymentVideoCharged"
  | "paymentExit"
  | "visitId"
  | "consentId"
  | "consentPending"
> = {
  route: "attract",
  selectedThemeId: null,
  selectedStyleId: null,
  capturedShots: [],
  lastShortCode: null,
  downloadUrl: null,
  printId: null,
  language: DEFAULT_LANGUAGE,
  videoStatus: "idle",
  videoUrl: null,
  videoJobId: null,
  selectedMotionIds: [],
  videoCaption: null,
  videoCaptionImage: null,
  paymentId: null,
  paymentWithVideo: false,
  paymentVideoCharged: false,
  paymentExit: null,
  visitId: null,
  consentId: null,
  consentPending: null,
};

export const useRouter = create<RouterState>((set, get) => ({
  ...INITIAL,
  boothLanguage: DEFAULT_LANGUAGE,
  // 라우트 전환 = 활동 이벤트 (BOOTH-87). 어트랙트를 떠나는 전환이 방문 시작 — 새 visitId 를 발급한 뒤 기록한다.
  go: (next) => {
    const s = get();
    const prev = s.route;
    if (prev === next) return;
    const visitId = visitTransition(prev, next) === "start" ? crypto.randomUUID() : s.visitId;
    set({ route: next, visitId });
    trackRoute(prev, next, { visitId, themeId: s.selectedThemeId, styleId: s.selectedStyleId, language: s.language });
  },
  // 손님이 헤더 토글로 고른 언어 — 이 세션까지만 유효하다(resetSession 이 부스 기본 언어로 되돌린다, BOOTH-116).
  setLanguage: (lang) => {
    set({ language: lang });
    track("language_set", { language: lang });
  },
  // 부스 설정에서 온 기본 언어 (BOOTH-116) — 어트랙트면 바로 보여주고, 손님 세션 중이면 기억만 해 둔다(언어가 중간에 바뀌지 않게).
  // 분기 규칙은 i18n/boothLanguage.ts `languageOnBoothConfig`.
  setBoothLanguage: (lang) =>
    set((s) => ({ boothLanguage: lang, language: languageOnBoothConfig(s.route, lang, s.language) })),
  setTheme: (id) => set({ selectedThemeId: id }),
  setStyle: (id) => set({ selectedStyleId: id }),
  addShot: (shot) =>
    set((s) => ({
      capturedShots: [
        ...s.capturedShots,
        { ...shot, transformedDataUrl: null, transformStatus: "pending", videoInputDataUrl: null },
      ],
    })),
  setTransformed: (id, dataUrl, status, videoInputDataUrl = null) =>
    set((s) => ({
      capturedShots: s.capturedShots.map((shot) =>
        shot.id === id
          ? { ...shot, transformedDataUrl: dataUrl, transformStatus: status, videoInputDataUrl }
          : shot
      ),
    })),
  clearShots: () => set({ capturedShots: [] }),
  setShortCode: (code, downloadUrl) => set({ lastShortCode: code, downloadUrl }),
  setPrintId: (id) => set({ printId: id }),
  // 언어는 부스 기본 언어로 복귀(BOOTH-116) — 손님이 바꾼 언어는 그 세션까지.
  // 분기 규칙은 i18n/boothLanguage.ts `languageOnReset`.
  // 방문 종료 기록은 리셋 전에 — 이유를 아는 호출자(무동작·처음으로)는 먼저 trackVisitEnd 를 부르고, 그 외는 reset.
  resetSession: () => {
    trackVisitEnd("reset");
    set((s) => ({ ...INITIAL, language: languageOnReset(s.boothLanguage) }));
  },
  setVideoJob: (jobId) =>
    set({ videoJobId: jobId, videoStatus: "generating", videoUrl: null }),
  setVideoReady: (url) => set({ videoUrl: url, videoStatus: "ready" }),
  setVideoFailed: () => set({ videoStatus: "failed" }),
  clearVideo: () =>
    set({ videoStatus: "idle", videoUrl: null, videoJobId: null }),
  toggleMotion: (id) => set((s) => ({ selectedMotionIds: toggleMotionPick(s.selectedMotionIds, id) })),
  clearMotions: () => set({ selectedMotionIds: [] }),
  setVideoCaption: (text, image) => set({ videoCaption: text, videoCaptionImage: image }),
  clearVideoCaption: () => set({ videoCaption: null, videoCaptionImage: null }),
  beginPayment: (withVideo) => set({ paymentId: null, paymentWithVideo: withVideo, paymentVideoCharged: false, paymentExit: null }),
  setPaymentPaid: (paymentId, items) => set({ paymentId, paymentVideoCharged: items.some((i) => i.kind === "video") }),
  setPaymentExit: (reason) => set({ paymentExit: reason }),
  clearPaymentExit: () => set({ paymentExit: null }),
  clearPayment: () => set({ paymentId: null, paymentWithVideo: false, paymentVideoCharged: false }),
  setConsentId: (id) => set({ consentId: id, consentPending: null }),
  setConsentPending: (req) => set({ consentPending: req }),
}));

// 텔레메트리가 현재 화면·방문·언어를 읽을 수 있게 스냅샷을 바인딩한다 — telemetry 모듈은 이 store 를 import 하지 않는다(순환 방지).
bindActivitySnapshot(() => {
  const s = useRouter.getState();
  return { route: s.route, visitId: s.visitId, themeId: s.selectedThemeId, styleId: s.selectedStyleId, language: s.language };
});
